import React, { useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, useRouteMatch } from 'react-router-dom';

import NoMatchRoute from '../../../../components/Shared/NoMatchRoute';
import { MARKETPLACE_DONATE_ROUTE, MARKETPLACE_ITEMS_ROUTE } from '../../constants/marketplaceRoute.constants';
import { getGiftHashId, getGiftProduct, getCanBeAccepted } from '../../store/data/data.selectors';
import useGoBack from '../../../../hooks/useGoBack';
import MarketplaceModule from '../../../MarketplaceModule';
import StyledHeader from '../StyledHeader/StyledHeader';
import GiftStatusBar from '../../../../components/GiftStatusBar/GiftStatusBar';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import useResponsive from '../../../../hooks/useResponsive';
import {
  GIFT_FLOW_EXCHANGE_ID,
  GIFT_FLOW_DONATE_ID,
  GIFT_FLOW_CONFIRMATION_ID,
  GIFT_FLOW_SHIPPING_ID,
} from '../../../../constants/giftFlow.constants';
import {
  getIsDonation,
  getProduct,
  getCountryId,
  getIsConfirmedFlow,
  getHasShippingAddress,
} from '../../store/giftFlow/giftFlow.selectors';
import { setCountryId, setShippingAddress } from '../../store/giftFlow/giftFlow.actions';
import useGiftActions from '../../hooks/useGiftActions';
import PageWithDataWrapper from '../../../../components/PageWithDataWrapper';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import { findItemById } from '../../../../helpers/list.helpers';
import { getStatuses as getStatusFlags } from '../../store/statuses/statuses.selectors';
import { getIsGiftDataLoaded } from '../../store/recipient.selectors';
import { getCountries } from '../../../../store/countries/countries.selectors';

import MarketplaceLayout from './MarketplaceLayout/MarketplaceLayout';

export interface IMarketplacePageProps {
  parentUrl: string;
}

const MarketplacePage = ({ parentUrl }: IMarketplacePageProps): JSX.Element => {
  const dispatch = useDispatch();
  const { url: marketplaceUrl } = useRouteMatch();
  const { isMobile } = useResponsive();
  const trackEvent = useTrackRecipient();

  const isGiftDataLoaded = useSelector(getIsGiftDataLoaded);
  const sentProduct = useSelector(getGiftProduct);
  const flowProduct = useSelector(getProduct);
  const isDonationFlow = useSelector(getIsDonation);
  const hashId = useSelector(getGiftHashId);
  const countries = useSelector(getCountries);
  const countryId = useSelector(getCountryId);
  const canAccept = useSelector(getCanBeAccepted);
  const isConfirmedFlow = useSelector(getIsConfirmedFlow);
  const hasShippingAddress = useSelector(getHasShippingAddress);
  const statusFlags = useSelector(getStatusFlags);
  const optionsData = flowProduct && flowProduct.optionsData;

  const { statuses, activeStatus, previousStepUrl, setActiveStatus } = useGiftStatuses(true);
  const handleGoBack = useGoBack(previousStepUrl);
  const { handleAccept } = useGiftActions(parentUrl);

  const handleAcceptProduct = useCallback(
    product => {
      let nextStepId = null;

      if (isConfirmedFlow) {
        nextStepId = GIFT_FLOW_CONFIRMATION_ID;
        if (product.isAddressRequired && !hasShippingAddress) {
          nextStepId = GIFT_FLOW_SHIPPING_ID;
        }
      }

      if (product.id === sentProduct.id) {
        dispatch(setCountryId(sentProduct.countryId));
      }

      handleAccept(product, nextStepId);
    },
    [isConfirmedFlow, hasShippingAddress, handleAccept, sentProduct, dispatch],
  );

  const handleMarketplaceCountryChange = useCallback(
    countryValue => {
      dispatch(setCountryId(countryValue));
      dispatch(setShippingAddress(null));
      const country = findItemById(countryValue)(countries);
      trackEvent('Country - Changed', { country: country?.name });
    },
    [dispatch, trackEvent, countries],
  );

  const commonMarketplaceProps = {
    hashId,
    sentProduct,
    optionsData,
    countryId,
    countries,
    isInternational: statusFlags.international,
    declaredData: statusFlags.declaredData,
    hasCustomMarketplace: statusFlags.hasCustomMarketplace,
    canAccept,
    onAccept: handleAcceptProduct,
    onChangeCountry: handleMarketplaceCountryChange,
  };

  const header = useMemo(
    () => (
      <StyledHeader onBackClick={handleGoBack}>
        <GiftStatusBar statuses={statuses} activeStatus={activeStatus} showStatuses={!isMobile} />
      </StyledHeader>
    ),
    [handleGoBack, statuses, activeStatus, isMobile],
  );

  usePageLoadEvent('Marketplace page - Opened');

  useEffect(() => {
    const marketplaceActiveStep = isDonationFlow ? GIFT_FLOW_DONATE_ID : GIFT_FLOW_EXCHANGE_ID;
    setActiveStatus(marketplaceActiveStep);
  }, [setActiveStatus, isDonationFlow]);

  return (
    <PageWithDataWrapper isLoaded={isGiftDataLoaded}>
      <MarketplaceLayout header={header}>
        <Switch>
          <Route path={`${marketplaceUrl}/${MARKETPLACE_ITEMS_ROUTE}`} exact>
            <MarketplaceModule {...commonMarketplaceProps} />
          </Route>
          <Route path={`${marketplaceUrl}/${MARKETPLACE_DONATE_ROUTE}`} exact>
            <MarketplaceModule {...commonMarketplaceProps} isDonation />
          </Route>
          <Route path="*" component={NoMatchRoute} />
        </Switch>
      </MarketplaceLayout>
    </PageWithDataWrapper>
  );
};

export default MarketplacePage;
